/**
 * Check for collision between items
 */
export default class ItemCollisionChecker {
    exceptionService = null;

    /**
     * Initializes the collision checker
     *
     * @param {TreeCollisionChecker} treeCollisionChecker
     * @param {CollisionExceptionService} exceptionService
     */
    constructor(treeCollisionChecker, exceptionService) {
        this.treeCollisionChecker = treeCollisionChecker;
        this.exceptionService = exceptionService;
    }

    /**
     * Check the given item against all its siblings
     *
     * @param {AbstractItem} item
     * @param {AbstractItem[]} otherItems
     * @return {AbstractCollision[]} Array with found collisions
     */
    check(item, otherItems) {
        const collisions = [];

        if (!item.supportsCollisions) return [];

        // The rule is disabled here because performance is more important inside collision detection
        // eslint-disable-next-line no-restricted-syntax
        for (const otherItem of otherItems) {
            const collision = this.checkItems(item, otherItem);

            if (collision) collisions.push(collision);
        }

        return collisions;
    }

    /**
     * @param {AbstractItem} item
     * @param {AbstractItem} otherItem
     * @return {AbstractCollision}
     */
    checkItems(item, otherItem) {
        if (!item.supportsCollisions) return null;
        if (!otherItem.supportsCollisions) return null;
        if (!this.exceptionService.canCollide(item, otherItem)) return null;
        if (otherItem.getMarginTree().getRoot() === null) return null;

        const supportMarginCollisions = this._supportMarginCollisions(item, otherItem);
        const supportBidirectionalCollisions = this._supportBidirectionalCollisions(item, otherItem);

        // with margin support & bidirectional support check both direction
        if (supportMarginCollisions && supportBidirectionalCollisions) {
            return this._checkCollision(item, otherItem) || this._checkCollision(otherItem, item);
        }

        // without bidirectional support the items will be checked with the greater safety margin as the leading item
        const orderedItems = this._sortItemsBySafetyMargin([item, otherItem]);
        return this._checkCollision(...orderedItems);
    }

    /**
     * @param {AbstractItem[]} items
     * @return {AbstractItem[]}
     * @private
     */
    _sortItemsBySafetyMargin(items) {
        return items.sort((a, b) => b.getSafetyMargin() - a.getSafetyMargin());
    }

    /**
     * @param {AbstractItem} leadingItem
     * @param {AbstractItem} otherItem
     *
     * @return {AbstractCollision|null}
     * @private
     */
    _checkCollision(leadingItem, otherItem) {
        const leadingTree = this._getLeadingTree(leadingItem, otherItem);
        const otherTree = this._getOtherTree(otherItem);

        return this.treeCollisionChecker.checkTree(leadingTree, otherTree);
    }

    /**
     * @param {AbstractItem} leadingItem
     * @param {AbstractItem} otherItem
     * @return {Tree}
     * @private
     */
    _getLeadingTree(leadingItem, otherItem) {
        return this._supportMarginCollisions(leadingItem, otherItem)
            ? leadingItem.getMarginTree()
            : leadingItem.getBagTree();
    }

    /**
     * @param {AbstractItem} otherItem
     * @return {Tree}
     * @private
     */
    _getOtherTree(otherItem) {
        return otherItem.getBagTree();
    }

    /**
     * @param {AbstractItem} item1
     * @param {AbstractItem} item2
     * @return {boolean}
     * @private
     */
    _supportMarginCollisions(item1, item2) {
        return item1.supportsMarginCollisions && item2.supportsMarginCollisions;
    }

    /**
     * @param {AbstractItem} item1
     * @param {AbstractItem} item2
     * @return {boolean}
     * @private
     */
    _supportBidirectionalCollisions(item1, item2) {
        return item1.supportsBidirectionalCollisions || item2.supportsBidirectionalCollisions;
    }
}
